/**
 *  PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2025 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */
/**
 * @file SidePanel.jsx - A component for sidepanel.
 */

import React, { useState, useEffect } from 'react';
import { IconButton, Tooltip, Stack, Divider, Typography, Box, Switch, Button } from '@mui/material';

import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

import PropTypes from 'prop-types';
import { graphActionButtons, nodeCreationButtons } from './SidePanelComponent';
import { iconSize, styles } from './SidePanelStyles';
import {
  AGENT_ICON,
  CONDITIONAL_EDGE,
  CONDITIONAL_EDGE_DESCRIPTION,
  CONDITIONAL_EDGE_TIP,
  CONFIGURE_NODE,
  SIDEPANEL_EXPANDED,
  SAVE,
  CREATE,
  RESET,
  PUBLISH,
  COPY
} from '../../constants/AgenticTextConstants';
import { sidePanel } from '../../constants/StyleConstants';

const STORAGE_KEY = SIDEPANEL_EXPANDED;

const SidePanel = ({
  handleNewGraph,
  handleReset,
  handleLoadGraph,
  handleSaveGraph,
  handleCopyGraph,
  handlePublishGraph,
  handleAddNode,
  handleConfigureNodeClick,
  isConditionalEdge,
  setIsConditionalEdge,
  canEditGraph
}) => {
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      setExpanded(stored === 'true');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, expanded);
  }, [expanded]);

  const actionHandlers = {
    create: handleNewGraph,
    reset: handleReset,
    load: handleLoadGraph,
    publish: handlePublishGraph,
    save: () => handleSaveGraph(''),
    copy: () => handleCopyGraph('')
  };

  const renderButton = ({ label, icon: Icon, onClick, canEdit }) => {
    const iconElement = Icon.name === AGENT_ICON ? <Icon widthSize={iconSize} /> : <Icon sx={{ fontSize: iconSize }} />;

    return expanded ? (
      <Button
        key={label}
        onClick={onClick}
        variant="text"
        startIcon={iconElement}
        sx={styles.sideButton}
        disabled={canEdit}
      >
        {label}
      </Button>
    ) : (
      <Tooltip key={label} title={label} placement="right" arrow>
        <IconButton onClick={onClick} sx={styles.iconButton} disabled={canEdit}>
          {iconElement}
        </IconButton>
      </Tooltip>
    );
  };

  const updatedGraphActionButtons = graphActionButtons.map((btn) => {
    if (
      btn.actionKey === SAVE ||
      btn.actionKey === CREATE ||
      btn.actionKey === RESET ||
      btn.actionKey === PUBLISH ||
      btn.actionKey === COPY
    ) {
      return { ...btn, canEdit: !canEditGraph };
    }
    return btn;
  });

  return (
    <Box component="aside" sx={styles.panelContainer(expanded)}>
      <Box sx={styles.panelContent}>
        {/* Graph Action Buttons */}
        <Stack spacing={1}>
          {updatedGraphActionButtons.map(({ label, icon, actionKey, canEdit }, i) =>
            renderButton({ label, icon, onClick: actionHandlers[actionKey], canEdit }, i)
          )}
        </Stack>

        <Divider sx={{ my: 2, borderColor: sidePanel.borderDividerColor }} />

        {/* Node Creation Buttons */}
        <Stack spacing={1}>
          {nodeCreationButtons.map(({ label, type, icon }, i) =>
            renderButton({ label, icon, onClick: () => handleAddNode(type), canEdit: !canEditGraph }, i)
          )}
        </Stack>

        <Divider sx={{ my: 2, borderColor: sidePanel.borderDividerColor }} />

        {/* Configure Button */}
        <Stack spacing={1}>
          {renderButton({
            label: CONFIGURE_NODE,
            icon: PermDataSettingIcon,
            onClick: handleConfigureNodeClick,
            canEdit: !canEditGraph
          })}
        </Stack>

        {/* Conditional Edge Toggle */}
        {canEditGraph && (
          <Tooltip disableHoverListener={expanded} title={CONDITIONAL_EDGE} placement="right" arrow>
            <Box sx={styles.conditionalBox(expanded, isConditionalEdge)}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexDirection={expanded ? 'row' : 'column'}
              >
                <Box display="flex" alignItems="center">
                  <AltRouteIcon fontSize="small" sx={{ mr: expanded ? 1 : 0, color: sidePanel.conditionalEdgeColor }} />
                  {expanded && (
                    <Typography variant="body2" fontWeight="bold" color={sidePanel.conditionalEdgeColor}>
                      {CONDITIONAL_EDGE}
                    </Typography>
                  )}
                </Box>
                <Switch
                  size="small"
                  checked={isConditionalEdge}
                  onChange={(e) => setIsConditionalEdge(e.target.checked)}
                  color="secondary"
                  disabled={!canEditGraph}
                />
              </Box>

              {expanded && (
                <>
                  <Box>
                    <Typography
                      variant="caption"
                      color={sidePanel.conditionalEdgeDescription}
                      mt={0.5}
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {CONDITIONAL_EDGE_DESCRIPTION}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color={sidePanel.conditionalEdgeTip}
                    mt={0.5}
                    sx={{ fontSize: '0.65rem' }}
                  >
                    {CONDITIONAL_EDGE_TIP}
                  </Typography>
                </>
              )}
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Collapse/Expand Toggle */}
      <Box sx={styles.collapseToggle}>
        <Tooltip title={expanded ? 'Collapse' : 'Expand'} arrow>
          <IconButton onClick={() => setExpanded(!expanded)} sx={{ color: '#f8fafc' }}>
            {expanded ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

SidePanel.propTypes = {
  handleNewGraph: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  handleLoadGraph: PropTypes.func.isRequired,
  handleSaveGraph: PropTypes.func.isRequired,
  handleCopyGraph: PropTypes.func.isRequired,
  handlePublishGraph: PropTypes.func.isRequired,
  handleAddNode: PropTypes.func.isRequired,
  handleConfigureNodeClick: PropTypes.func.isRequired,
  isConditionalEdge: PropTypes.bool.isRequired,
  setIsConditionalEdge: PropTypes.func.isRequired,
  canEditGraph: PropTypes.bool.isRequired
};

export default SidePanel;
